import { createRouter, type RouterHistory } from '@tanstack/react-router';

import { sessionQueryKey } from '@/features/auth/api';
import { routeTree } from '@/routeTree.gen';

import { createQueryClient } from './query-client';
import { NotFound, RouteError, RoutePending } from './route-states';

const PUBLIC_PATHS = /^\/(login|forgot-password|reset-password\/|invite\/)/;

/**
 * Builds the query client and the router together so a 401 anywhere can send people to sign in.
 * Tests pass a memory history to render the real app at any URL.
 */
export function createAppRouter(options: { history?: RouterHistory } = {}) {
  const queryClient = createQueryClient(() => {
    const { pathname, href } = router.state.location;
    if (PUBLIC_PATHS.test(pathname)) return;
    queryClient.removeQueries({ queryKey: sessionQueryKey });
    void router.navigate({ to: '/login', search: { redirect: href } });
  });

  const router = createRouter({
    routeTree,
    context: { queryClient },
    ...(options.history ? { history: options.history } : {}),
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: RouteError,
    defaultPendingComponent: RoutePending,
    defaultNotFoundComponent: NotFound,
    scrollRestoration: true,
  });

  return { router, queryClient };
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>['router'];
  }
}
